import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import styles from './PricesTab.module.scss';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import { Label } from 'common/components/texts/Texts';
import MoneyInput from 'common/components/moneyInput/MoneyInput';
import { useDispatch, useSelector } from 'react-redux';
import { calculateLineTotals, updateLine } from 'store/budget/action';
import SelectInput from 'common/components/selectInput/SelectInput';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LinesDataProvider } from '../../../LinesDataProvider';
import { DEFAULT_DECIMAL_PLACES } from 'Config';
import MathUtils from 'common/services/MathUtils';
import { Reducers } from 'store/types';
import { BudgetPDFVisibility } from 'api/elements/Models/ElementDto';
import InfoIcon from 'common/components/infoIcon/InfoIcon';
import InformationModal from 'common/components/modal/informationModal/InformationModal';
import ExpressionSystem from 'common/components/expressionButton/ExpressionSystem';
import { getExpressionParameters } from '../../../utils';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import FamiliesService from 'api/families/FamiliesService';
import BudgetsService from 'api/budgets/BudgetsService';
import DiscountSectionsModal, { DiscountSection } from './DiscountSectionsModal';

interface Props {
    line: BudgetLineDto;
    disabled: boolean;
}

function PricesTab({ line, disabled }: Props): JSX.Element | null {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { taxes } = useContext(LinesDataProvider);
    const decimalPlaces = useSelector<Reducers, number>(x => x.budget.budget.decimalPlaces ?? DEFAULT_DECIMAL_PLACES);

    const allLines = useSelector<Reducers, BudgetLineDto[]>(x => x.budget.lines);

    const [currentInfoTooltip, setCurrentInfoTooltip] = useState<{ title: string; message: string } | null>(null);
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [discountSections, setDiscountSections] = useState<DiscountSection[]>([]);
    const [elementBasePrice, setElementBasePrice] = useState<number | null>(null);

    useEffect(() => {
        if (!line.elementId || line.isCustomElement) {
            setElementBasePrice(null);
            return;
        }
        let cancelled = false;
        BudgetsService.getElementParameters(line.elementId, false)
            .then(data => {
                if (!cancelled) setElementBasePrice(data.price ?? null);
            })
            .catch(() => {
                if (!cancelled) setElementBasePrice(null);
            });
        return () => { cancelled = true; };
    }, [line.elementId, line.isCustomElement]);

    const isGrossPriceModified = elementBasePrice != null
        && MathUtils.round(line.grossUnitPrice ?? 0, decimalPlaces)
            !== MathUtils.round(elementBasePrice, decimalPlaces);
    const grossPriceTooltip = elementBasePrice == null
        ? undefined
        : isGrossPriceModified
            ? t('budgets.budget.lines.header.gross_unit_price_modified', { price: MathUtils.round(elementBasePrice, decimalPlaces) })
            : t('budgets.budget.lines.header.gross_unit_price_is_base');

    const childSections = useMemo(() =>
        allLines.filter(l => l.parentKey === line.key && l.lineType === BudgetLineType.SECTION),
        [allLines, line.key]
    );

    const hasChildSections = childSections.length > 0;

    const taxesOptions = taxes.map(t => ({ selectedLabel: t.value?.toString() + ' %', label: t.description, value: t.id }));

    const onChangeValue = (newLine: Partial<BudgetLineDto>) => {
        dispatch(updateLine({ key: line.key, ...newLine }));
        dispatch(calculateLineTotals(line.key));
    }

    const deriveSectionDiscountState = useCallback((sectionKey: string) => {
        const children = allLines.filter(l =>
            l.parentKey === sectionKey &&
            (l.lineType === BudgetLineType.ELEMENT || l.lineType === BudgetLineType.COMPONENT_ELEMENT)
        );
        const childExprs = children.map(c => c.discountPercentageExpression);
        const hasDiscountApplied = childExprs.some(e => e != null && e.trim().length > 0);
        const uniqueExprs = new Set(childExprs.map(e => e ?? ''));
        const hasInconsistentFormulas = hasDiscountApplied && uniqueExprs.size > 1;
        return { hasDiscountApplied, hasInconsistentFormulas };
    }, [allLines]);

    const onOpenDiscountModal = useCallback(async () => {
        const buildSections = (familyParams?: { id?: string; parameterName?: string | null; title?: string | null; isEligibleForDiscount?: boolean }[]) => {
            return childSections.map(s => {
                const familyParam = familyParams
                    ? (s.sourceFamilyParameterId
                        ? familyParams.find(fp => fp.id === s.sourceFamilyParameterId)
                        : familyParams.find(fp => fp.parameterName === s.designation || fp.title === s.designation))
                    : undefined;
                const { hasDiscountApplied, hasInconsistentFormulas } = deriveSectionDiscountState(s.key);
                return {
                    key: s.key,
                    designation: s.designation,
                    isEligibleForDiscount: familyParam?.isEligibleForDiscount ?? false,
                    hasDiscountApplied,
                    hasInconsistentFormulas,
                };
            });
        };

        try {
            let familyId: string | null | undefined = line.parameters?.find(p => p.familyId)?.familyId ?? null;

            if (!familyId) {
                const sectionWithParam = childSections.find(s => s.sourceFamilyParameterId);
                if (sectionWithParam?.sourceFamilyParameterId) {
                    familyId = await FamiliesService.getFamilyIdByParameter(sectionWithParam.sourceFamilyParameterId);
                }
            }

            if (!familyId) {
                setDiscountSections(buildSections());
                setShowDiscountModal(true);
                return;
            }

            const familyParams = await FamiliesService.getFamilyParameters(familyId);
            setDiscountSections(buildSections(familyParams));
            setShowDiscountModal(true);
        } catch {
            setDiscountSections(buildSections());
            setShowDiscountModal(true);
        }
    }, [childSections, line.parameters, deriveSectionDiscountState]);

    const onSaveDiscount = useCallback((expression: string | null, value: number | null | undefined, applyToLine: boolean, selectedSectionKeys: string[]) => {
        if (applyToLine) {
            dispatch(updateLine({ key: line.key, discountPercentage: value ?? 0, discountPercentageExpression: expression }));
            dispatch(calculateLineTotals(line.key));
        }

        for (const sectionKey of selectedSectionKeys) {
            const sectionChildren = allLines.filter(l =>
                l.parentKey === sectionKey &&
                (l.lineType === BudgetLineType.ELEMENT || l.lineType === BudgetLineType.COMPONENT_ELEMENT)
            );
            for (const child of sectionChildren) {
                dispatch(updateLine({
                    key: child.key,
                    discountPercentage: value ?? 0,
                    discountPercentageExpression: expression,
                }));
                dispatch(calculateLineTotals(child.key));
            }
        }
        setShowDiscountModal(false);
    }, [allLines, line.key, dispatch]);

    return (
        <div className={styles.container}>
            <Row className='mb-3'>
                <Col>
                    <Label space>{t('elements.budget_pdf_visibility')}</Label>
                    <SelectInput
                        value={line.pdfVisibility}
                        options={[
                            {
                                label: t('elements.budget_pdf_visibility_options.element_and_price_visible'),
                                value: BudgetPDFVisibility.ELEMENT_AND_PRICE_VISIBLE
                            },
                            {
                                label: t('elements.budget_pdf_visibility_options.price_invisible'),
                                value: BudgetPDFVisibility.PRICE_INVISIBLE
                            },
                            {
                                label: t('elements.budget_pdf_visibility_options.element_invisible'),
                                value: BudgetPDFVisibility.ELEMENT_INVISIBLE
                            },
                        ]}
                        disabled={disabled}
                        onChange={(v) => onChangeValue({ pdfVisibility: v ?? null })}
                        removeRemoveButton
                    />
                </Col>
            </Row>
            <Row>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space className={styles.centerLine}>{t('budgets.budget.lines.header.gross_unit_price')}<InfoIcon onClick={() => setCurrentInfoTooltip({ title: t('budgets.budget.lines.header.gross_unit_price'), message: t('budgets.budget.lines.header.gross_unit_price_tooltip') })} /></Label>
                    <MoneyInput
                        value={line.grossUnitPrice}
                        onBlur={(v) => onChangeValue({ grossUnitPrice: v ?? 0, grossUnitPriceExpression: v !== line.grossUnitPrice ? null : line.grossUnitPriceExpression })}
                        unitPrefix="€"
                        decimalScale={decimalPlaces}
                        disabled={disabled}
                        unitPrefixModified={isGrossPriceModified}
                        unitPrefixTooltip={grossPriceTooltip}
                        expressionButton={{
                            title: ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.gross_unit_price')]),
                            value: line.grossUnitPriceExpression ?? '',
                            loadParameters: () => getExpressionParameters(line.key),
                            onChange: (expr, value) => onChangeValue({ grossUnitPrice: value ?? 0, grossUnitPriceExpression: expr })
                        }}
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space className={styles.centerLine}>{t('budgets.budget.lines.header.discount')}<InfoIcon onClick={() => setCurrentInfoTooltip({ title: t('budgets.budget.lines.header.discount'), message: t('budgets.budget.lines.header.discount_tooltip') })} /></Label>
                    <MoneyInput
                        value={line.discountPercentage}
                        onBlur={(v) => onChangeValue({ discountPercentage: v ?? 0, discountPercentageExpression: v !== line.discountPercentage ? null : line.discountPercentageExpression })}
                        unitPrefix="%"
                        disabled={disabled}
                        expressionButton={{
                            title: ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.discount')]),
                            value: line.discountPercentageExpression ?? '',
                            loadParameters: () => getExpressionParameters(line.key),
                            onChange: (expr, value) => onChangeValue({ discountPercentage: value ?? 0, discountPercentageExpression: expr }),
                            ...(hasChildSections ? { onOpen: onOpenDiscountModal } : {}),
                        }}
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space className={styles.centerLine}>{t('budgets.budget.lines.header.aggregated_elements')}<InfoIcon onClick={() => setCurrentInfoTooltip({ title: t('budgets.budget.lines.header.aggregated_elements'), message: t('budgets.budget.lines.header.aggregated_elements_tooltip') })} /></Label>
                    <MoneyInput
                        value={line.unitPriceWithChildren}
                        unitPrefix="€"
                        decimalScale={decimalPlaces}
                        disabled
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space className={styles.centerLine}>{t('budgets.budget.lines.header.total_cost_unit_price')}<InfoIcon onClick={() => setCurrentInfoTooltip({ title: t('budgets.budget.lines.header.total_cost_unit_price'), message: t('budgets.budget.lines.header.total_cost_unit_price_tooltip') })} /></Label>
                    <MoneyInput
                        value={MathUtils.plus(line.grossUnitPrice, MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1, line.unitPriceWithChildren)}
                        unitPrefix="€"
                        decimalScale={decimalPlaces}
                        disabled
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space className={styles.centerLine}>{t('budgets.budget.lines.header.margin')}<InfoIcon onClick={() => setCurrentInfoTooltip({ title: t('budgets.budget.lines.header.margin'), message: t('budgets.budget.lines.header.margin_tooltip') })} /></Label>
                    <MoneyInput
                        value={line.marginPercentage}
                        onBlur={(v) => onChangeValue({ marginPercentage: v ?? 0, marginPercentageExpression: v !== line.marginPercentage ? null : line.marginPercentageExpression })}
                        unitPrefix="%"
                        disabled={disabled}
                        expressionButton={{
                            title: ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.margin')]),
                            value: line.marginPercentageExpression ?? '',
                            loadParameters: () => getExpressionParameters(line.key),
                            onChange: (expr, value) => onChangeValue({ marginPercentage: value ?? 0, marginPercentageExpression: expr })
                        }}
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space className={styles.centerLine}>{t('budgets.budget.lines.header.decennial_insurance')}<InfoIcon onClick={() => setCurrentInfoTooltip({ title: t('budgets.budget.lines.header.decennial_insurance'), message: t('budgets.budget.lines.header.decennial_insurance_tooltip') })} /></Label>
                    <MoneyInput
                        value={line.decennialInsurancePercentage}
                        onBlur={(v) => onChangeValue({ decennialInsurancePercentage: v ?? 0, decennialInsurancePercentageExpression: v !== line.decennialInsurancePercentage ? null : line.decennialInsurancePercentageExpression })}
                        unitPrefix="%"
                        disabled={disabled}
                        expressionButton={{
                            title: ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.decennial_insurance')]),
                            value: line.decennialInsurancePercentageExpression ?? '',
                            loadParameters: () => getExpressionParameters(line.key),
                            onChange: (expr, value) => onChangeValue({ decennialInsurancePercentage: value ?? 0, decennialInsurancePercentageExpression: expr })
                        }}
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space className={styles.centerLine}>{t('budgets.budget.lines.header.price_unity')}<InfoIcon onClick={() => setCurrentInfoTooltip({ title: t('budgets.budget.lines.header.price_unity'), message: t('budgets.budget.lines.header.price_unity_tooltip') })} /></Label>
                    <MoneyInput
                        value={line.unitPrice}
                        unitPrefix="€"
                        decimalScale={decimalPlaces}
                        disabled
                        expressionButton={{
                            title: ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.price_unity')]),
                            value: line.unitPriceExpression ?? '',
                            loadParameters: () => getExpressionParameters(line.key),
                            onChange: (expr, value) => onChangeValue({ unitPrice: value ?? 0, unitPriceExpression: expr })
                        }}
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space className={styles.centerLine}>{t('budgets.budget.lines.header.total_without_tax')}<InfoIcon onClick={() => setCurrentInfoTooltip({ title: t('budgets.budget.lines.header.total_without_tax'), message: t('budgets.budget.lines.header.total_without_tax_tooltip') })} /></Label>
                    <MoneyInput
                        value={line.totalExcludingTax}
                        unitPrefix="€"
                        decimalScale={decimalPlaces}
                        disabled
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space>{t('budgets.budget.lines.header.tax')}</Label>
                    <SelectInput
                        value={line.taxId}
                        options={taxesOptions}
                        onChange={v => onChangeValue({ taxId: v, taxValue: taxes.find(x => x.id === v)?.value ?? 0 })}
                        disabled={disabled}
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space>{t('budgets.budget.lines.header.total_with_tax')}</Label>
                    <MoneyInput
                        value={line.totalIncludingTax}
                        unitPrefix="€"
                        decimalScale={decimalPlaces}
                        disabled
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space className={styles.centerLine}>{t('budgets.budget.lines.header.net_profit')}<InfoIcon onClick={() => setCurrentInfoTooltip({ title: t('budgets.budget.lines.header.net_profit'), message: t('budgets.budget.lines.header.net_profit_tooltip') })} /></Label>
                    <MoneyInput
                        value={MathUtils.multiply(MathUtils.minus(line.unitPrice, MathUtils.plus(line.grossUnitPrice, MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1, line.unitPriceWithChildren)), line.quantity ?? 0)}
                        unitPrefix="€"
                        decimalScale={decimalPlaces}
                        disabled
                    />
                </Col>
                <Col xxl={3} xl={4} lg={6} md={12} sm={12} className='mb-3'>
                    <Label space className={styles.centerLine}>{t('budgets.budget.lines.header.net_profit_percentage')}<InfoIcon onClick={() => setCurrentInfoTooltip({ title: t('budgets.budget.lines.header.net_profit_percentage'), message: t('budgets.budget.lines.header.net_profit_percentage_tooltip') })} /></Label>
                    <MoneyInput
                        value={line.totalExcludingTax !== 0 ? MathUtils.multiply(MathUtils.divide(MathUtils.multiply(MathUtils.minus(line.unitPrice, MathUtils.plus(line.grossUnitPrice, MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1, line.unitPriceWithChildren)), line.quantity ?? 0), line.totalExcludingTax), 100) : 0}
                        unitPrefix="%"
                        decimalScale={decimalPlaces}
                        disabled
                    />
                </Col>
            </Row>
            <InformationModal
                title={currentInfoTooltip?.title}
                message={currentInfoTooltip?.message || ''}
                onClose={() => setCurrentInfoTooltip(null)}
                isOpen={Boolean(currentInfoTooltip)}
            />
            {showDiscountModal && (
                <DiscountSectionsModal
                    isOpen={true}
                    onClose={() => setShowDiscountModal(false)}
                    sections={discountSections}
                    expressionValue={line.discountPercentageExpression ?? ''}
                    expressionTitle={ExpressionSystem.formatParameterName([line.designation, t('budgets.budget.lines.header.discount')])}
                    loadParameters={() => getExpressionParameters(line.key)}
                    onSave={onSaveDiscount}
                    disabled={disabled}
                />
            )}
        </div>
    );
}

export default PricesTab;
